import { Link, useFetcher, useLocation, useNavigate, useRouteLoaderData } from 'react-router';
import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import Notifications from '../features/studio/notifications/Notifications';
import { DropdownMenu } from '../ui/kit/components/navigation';
import { Avatar, Button, IconButton } from '../ui/kit/components/primitives';
import { Icon } from '../ui/kit/components/icons';
import { Modal } from '../ui/kit/components/overlays';
import '../ui/styles/components.css';
import '../features/projects/home.css';
import { ModuleLoader } from './loaders/Loaders';
import type { AuthUser } from '../lib/types';
import type { RootLoaderData } from '../routes/rootLayout';

export type FloorNavKey = 'productions' | 'viral' | 'guild' | 'cost' | 'review' | 'scheduler' | 'plans' | 'settings';
const ReviewDetail = lazy(() => import('../features/studio/reviews/ReviewDetail'));
const NAV: Array<{ key: FloorNavKey; label: string; to: string }> = [
  { key: 'productions', label: 'Productions', to: '/' },
  { key: 'viral', label: 'Go Viral', to: '/viral' },
  { key: 'guild', label: 'Guild', to: '/guild' },
  { key: 'cost', label: 'Cost', to: '/cost' },
  { key: 'review', label: 'Review', to: '/reviews' },
  { key: 'scheduler', label: 'Scheduler', to: '/scheduler' },
  { key: 'plans', label: 'Plans', to: '/pricing' }
];

function LockedLink({ item, open, onToggle, canStart, canStartFree, onPricing }: { item: { key: FloorNavKey; label: string }; open: boolean; onToggle: () => void; canStart: boolean; canStartFree: boolean; onPricing: boolean }) {
  const starter = useFetcher();
  const hint = `studio-floor-lock-${item.key}`;
  return <span className={`studio-floor-locked${open ? ' is-open' : ''}`}>
    <button type="button" className="bs-button bs-button--ghost bs-button--sm" aria-disabled="true" aria-expanded={open} aria-describedby={hint} onClick={onToggle}>
      <span>{item.label}</span><Icon name="lock" size={12} aria-hidden="true" />
    </button>
    <span className="studio-floor-lock-hint" id={hint}>
      <span>{canStart ? `${item.label} opens once your studio has a plan.` : `${item.label} opens once your studio owner chooses a plan.`}</span>
      {(canStartFree || !onPricing) && <span className="studio-floor-lock-actions">
        {canStartFree && <Button size="sm" variant="primary" loading={starter.state !== 'idle'} onClick={() => starter.submit({ intent: 'start-free' }, { method: 'post', action: '/pricing' })}>Start free</Button>}
        {!onPricing && <Link className="bs-button bs-button--ghost bs-button--sm" to="/pricing">See plans</Link>}
      </span>}
    </span>
  </span>;
}

export interface FloorHeaderProps {
  active: FloorNavKey;
  user?: (Partial<Pick<AuthUser, 'studios' | 'invitations'>> & { name?: string | null; email?: string | null }) | null;
  onError?: (message: string) => void;
}

export default function FloorHeader({ active, user }: FloorHeaderProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [reviewTarget, setReviewTarget] = useState<{ projectId: string; presentationId: string } | null>(null);
  const root = useRouteLoaderData('root') as RootLoaderData | undefined;
  const pending = root?.auth.planStatus === 'pending';
  const canStart = root?.auth.user?.role === 'owner';
  const [lockOpen, setLockOpen] = useState<FloorNavKey | null>(null);
  const linksRef = useRef<HTMLElement>(null);
  useEffect(() => {
    if (!lockOpen) return;
    const onPointer = (event: PointerEvent) => { if (!linksRef.current?.contains(event.target as Node)) setLockOpen(null); };
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') setLockOpen(null); };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('pointerdown', onPointer); document.removeEventListener('keydown', onKey); };
  }, [lockOpen]);
  const openSettings = () => navigate('/settings');
  const studios = user?.studios || [];
  const current = studios.find(studio => studio.active);
  const waiting = user?.invitations?.length || 0;

  return <header className="bs-theme studio-floor-header">
    <Link className="studio-floor-brand" to="/"><img src="/brand/frameon-mark.svg" alt="" /><span>Frameon</span></Link>
    <nav ref={linksRef} className="studio-floor-links" aria-label="Studio">{NAV.map(item => pending && item.key !== 'plans'
      ? <LockedLink key={item.key} item={item} open={lockOpen === item.key} onToggle={() => setLockOpen(lockOpen === item.key ? null : item.key)} canStart={canStart} canStartFree={canStart && root?.auth.freePlan === true} onPricing={location.pathname === '/pricing'} />
      : <Link key={item.key} to={item.to} className="bs-button bs-button--ghost bs-button--sm" aria-current={item.key === active ? 'page' : undefined}>{item.label}</Link>)}</nav>
    <div className="studio-floor-actions">
      <span className="studio-floor-bell">
        <IconButton icon="bell" label={waiting ? `Notifications, ${waiting} studio ${waiting === 1 ? 'invitation' : 'invitations'} waiting` : 'Notifications'} variant="ghost" onClick={() => setNotificationsOpen(true)} />
        {waiting > 0 && <span className="studio-floor-bell-count" aria-hidden="true">{waiting}</span>}
      </span>
      <IconButton icon="settings" label="Settings" variant="ghost" onClick={openSettings} />
      <DropdownMenu label={`Account menu for ${user?.name || 'your account'}`} items={[
        { label: user?.email || user?.name || 'Signed in', disabled: true, onSelect: () => {} },
        ...(studios.length > 1 ? [{ label: 'Change studio', icon: 'users' as const, description: current ? `In ${current.name} · ${studios.length} studios` : `${studios.length} studios`, onSelect: () => navigate(`/studios?next=${encodeURIComponent(location.pathname)}`) }] : []),
        { label: 'Studio settings', onSelect: () => { void openSettings(); } },
        { label: 'Sign out', danger: true, onSelect: () => { navigate('/signout'); } }
      ]}><Avatar name={user?.name || 'User'} size="sm" /></DropdownMenu>
    </div>
    {notificationsOpen && <Notifications onClose={() => setNotificationsOpen(false)} onReview={async (projectId, presentationId) => { setNotificationsOpen(false); setReviewTarget({ projectId, presentationId }); }} />}
    {reviewTarget && <Suspense fallback={<Modal open title="Loading feedback" onOpenChange={() => setReviewTarget(null)}><ModuleLoader module="reviews" layout="compact" title="Loading presentation…" /></Modal>}><ReviewDetail {...reviewTarget} onClose={() => setReviewTarget(null)} /></Suspense>}
  </header>;
}
